/* eslint-disable max-lines */
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Collapse,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Slide,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { filter, find, includes, isEmpty, isNil, map, reduce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { lighten, useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { format } from 'date-fns';

import { loadRequestDetails } from 'store/thunks/orderStatusThunks';
import LoadingModule from 'common/components/LoadingModule';
import gender from 'common/constants/gender';
import VerifiedTag from 'common/modules/VerifiedTag';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import { ProviderVerificationStatus } from 'enums/common.enum';
import {
  casingFormat,
  formatReviewReason,
  formatShipByDate,
  normalizePhoneNumber,
} from 'common/utils';
import { ShowDefaultPartnerContact } from 'common/utils/featureFlags';
import formatPrxSalesRep, { PRX_SALES_REP_LABEL } from 'common/utils/formatPrxSalesRep';
import choosePartnerContactForOrder from 'modules/OrderView/utils/choosePartnerContactForOrder';
import DefaultContactChip from 'modules/PatientView/components/DefaultContactChip';
import PlaceholderEmailMarker from 'modules/PatientView/components/PartnerContacts/PlaceholderEmailMarker';
import EScriptOrdersProgressTab from './EScriptOrdersProgressTab';

const Transition = forwardRef((props, ref) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Slide direction='up' ref={ref} {...props} timeout={300} />
));

const EScriptRequestView = ({
  referenceId,
  requestId,
  open,
  onClose,
  orderId,
  fromPatientProfile,
  mpi,
  partnerId,
}) => {
  const dispatch = useDispatch();
  const { pages, currentPage } = useSelector(({ orderStatus: { eScriptOrders } }) => eScriptOrders);
  const patientOrders = useSelector(({ patient }) => patient[mpi]?.orders);
  const patientOrderHistory = useSelector(({ patient }) => patient[mpi]?.orderHistory);
  const [viewMoreElements, setViewMoreElements] = useState([]);
  const [openStates, setOpenStates] = useState({});
  const [expandedPrescriptions, setExpandedPrescriptions] = useState({});
  const requests = pages[currentPage] || [];
  const requestDetails =
    find(requests, { requestId })?.requestDetails ||
    find(patientOrders, { orderId })?.orderDetails ||
    find(patientOrderHistory, { orderId })?.orderDetails;

  const patient = requestDetails?.patient;
  // Same rule as the Order View patient card, so both screens show one value.
  const orderContact = choosePartnerContactForOrder({
    patient,
    partnerUuid: partnerId,
    isDefaultContactShown: ShowDefaultPartnerContact,
  });
  const prescription = requestDetails?.prescriptions;
  const transferPharmacies = requestDetails?.prescriptions[0]?.transferPharmacies;
  const practice = requestDetails?.practice;
  const prxSalesRep = requestDetails?.prxSalesRep;
  const events = requestDetails?.events || [];
  const currentAddress = requestDetails?.shippingAddress;
  const deliveryInstructions = requestDetails?.deliveryInstructions;

  const fieldToggleHandler = useMemo(() => {
    const pharmacyDetails = reduce(
      transferPharmacies,
      (acc, pharmacy, index) => {
        const key = `Pharmacy${pharmacy.npi}-${index}`;
        acc[key] = key;

        return acc;
      },
      {}
    );

    return {
      Patient: 'Patient',
      Prescription: 'Prescription',
      PrescriptionDetails: 'PrescriptionDetails',
      Pharmacy: 'Pharmacy',
      Prescriber: 'Prescriber',
      ProviderDetails: 'ProviderDetails',
      ...pharmacyDetails,
    };
  }, [transferPharmacies]);

  const {
    isOpenPatient,
    isOpenPrescription,
    isOpenPrescriptionDetails,
    isOpenPharmacy,
    isOpenPrescriber,
    isOpenProviderDetails,
    ...dynamicOpenStates
  } = openStates;

  const upsertViewMoreElements = (element) => {
    if (includes(viewMoreElements, element)) {
      const updatedElements = filter(
        viewMoreElements,
        (currentElement) => element !== currentElement
      );

      return setViewMoreElements([...updatedElements]);
    }

    viewMoreElements.push(element);
    return setViewMoreElements([...viewMoreElements]);
  };

  const togglePrescriptionExpansion = (prescriptionId) => {
    setExpandedPrescriptions((prev) => ({
      ...prev,
      [prescriptionId]: !prev[prescriptionId],
    }));
  };

  useEffect(() => {
    setOpenStates(
      Object.keys(fieldToggleHandler).reduce((acc, key) => {
        acc[`isOpen${key}`] = includes(viewMoreElements, fieldToggleHandler[key]);
        return acc;
      }, {})
    );
  }, [viewMoreElements, fieldToggleHandler]);

  const eventData = find(events, ({ actionItemType }) =>
    ['ORDER_SUBMITTED_FROM', 'REFILL_ORDER_CREATED_FROM'].some((type) =>
      actionItemType.includes(type)
    )
  )?.interactionData;

  const shippingAddress =
    eventData?.originalEventPayload?.fulfillmentDetails?.shippingAddress ||
    eventData?.constructedPayload?.fulfillmentDetails?.shippingAddress ||
    eventData?.originalEventPayload?.shippingInput;

  useEffect(() => {
    if (open) {
      dispatch(
        loadRequestDetails({ fromPatientProfile, referenceId, requestId, orderId, mpi, partnerId })
      );
    }
  }, [dispatch, referenceId, requestId, orderId, fromPatientProfile, mpi, open, partnerId]);

  return (
    <Dialog open={open} fullScreen TransitionComponent={Transition}>
      <Grid
        container
        sx={{ px: 3, py: 1, bgcolor: 'primary.light' }}
        justifyContent='space-between'
        alignItems='center'
      >
        <Typography
          component='h2'
          variant='h6'
          fontWeight='bold'
          color={perfectRxTheme.palette.text.light}
        >
          {fromPatientProfile ? `Order - ${orderId}` : `Request - ${referenceId}`}
        </Typography>

        <IconButton onClick={onClose} aria-label='Close Dialog'>
          <CloseIcon style={{ color: perfectRxTheme.palette.text.light }} />
        </IconButton>
      </Grid>

      <DialogContent>
        {isNil(requestDetails) ? (
          <LoadingModule height='25vh' />
        ) : (
          <Grid container direction='row' sx={{ px: 4 }}>
            <Grid item md={7}>
              <Grid sx={{ pb: 1 }}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <Grid container direction='row' alignItems='center' sx={{ mb: 0.5 }}>
                    <Typography
                      variant='subtitle2'
                      fontWeight='bold'
                      color='primary.main'
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}
                    >
                      <PersonIcon fontSize='small' />
                      Patient Information
                    </Typography>
                    <IconButton
                      aria-label='Toggle Details patient'
                      onClick={() => upsertViewMoreElements(fieldToggleHandler.Patient)}
                      sx={{
                        bgcolor: 'primary.100',
                        '&:hover': { bgcolor: 'primary.200' },
                      }}
                    >
                      {isOpenPatient ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </Grid>

                  <Grid sx={{ pl: 4 }}>
                    <ItemLabelValue label='MPI' value={patient?.mpi} />
                    <ItemLabelValue
                      label='Name'
                      value={`${patient?.lastName}, ${patient?.firstName}`}
                    />
                    <ItemLabelValue label='External Id' value={patient?.externalPatientId} />
                    <Collapse in={isOpenPatient} timeout='auto' unmountOnExit>
                      <ItemLabelValue label='DOB' value={patient?.dob} />
                      <ItemLabelValue label='Gender' value={gender[patient?.gender]} />
                      <ItemLabelValue
                        label='Email'
                        value={orderContact.email}
                        labelAdornment={
                          orderContact.isDefaultEmailShown && (
                            <DefaultContactChip contactKind='email' />
                          )
                        }
                        valueAdornment={
                          orderContact.isPlaceholderEmailShown && (
                            <PlaceholderEmailMarker sx={{ ml: 1 }} />
                          )
                        }
                      />
                      <ItemLabelValue
                        label='Phone'
                        value={normalizePhoneNumber(orderContact.phoneNumber)}
                        labelAdornment={
                          orderContact.isDefaultPhoneShown && (
                            <DefaultContactChip contactKind='phone' />
                          )
                        }
                      />
                    </Collapse>
                  </Grid>
                </Box>
              </Grid>

              <Grid sx={{ pb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <Typography
                    variant='subtitle2'
                    fontWeight='bold'
                    color='primary.main'
                    sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <BusinessIcon fontSize='small' />
                    Practice Information
                  </Typography>
                  <Grid sx={{ pl: 4 }}>
                    <ItemLabelValue label='Practice Id' value={practice?.practiceId} />
                    <ItemLabelValue label='Name' value={casingFormat(practice?.practiceName)} />
                  </Grid>
                </Box>

                {/* POM-6494: the order partner's current internal Sales User. */}
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <Typography
                    variant='subtitle2'
                    fontWeight='bold'
                    color='primary.main'
                    sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <SupportAgentIcon fontSize='small' />
                    Partner Information
                  </Typography>
                  <Grid sx={{ pl: 4 }}>
                    <ItemLabelValue
                      label='Name'
                      value={casingFormat(requestDetails?.partner?.partnerName)}
                    />
                    <ItemLabelValue
                      label={PRX_SALES_REP_LABEL}
                      value={formatPrxSalesRep(prxSalesRep ?? null)}
                    />
                  </Grid>
                </Box>
              </Grid>

              <Grid sx={{ pb: 2 }}>
                <Grid sx={{ pt: 1 }}>
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200',
                    }}
                  >
                    <Typography
                      variant='subtitle2'
                      fontWeight='bold'
                      color='primary.main'
                      sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <LocationOnIcon fontSize='small' />
                      Initial fulfillment address
                    </Typography>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      {[
                        shippingAddress?.addressLine1,
                        shippingAddress?.addressLine2,
                        shippingAddress?.addressCity,
                        shippingAddress?.addressState,
                        shippingAddress?.addressZip,
                      ]
                        .filter(Boolean)
                        .join(', ') || 'No address provided'}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200',
                    }}
                  >
                    <Typography
                      variant='subtitle2'
                      fontWeight='bold'
                      color='primary.main'
                      sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <LocalShippingIcon fontSize='small' />
                      Current fulfillment address
                    </Typography>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      {[
                        currentAddress?.line1,
                        currentAddress?.line2,
                        currentAddress?.city,
                        currentAddress?.state,
                        currentAddress?.zip,
                      ]
                        .filter(Boolean)
                        .join(', ') || 'No fulfillment address available'}
                    </Typography>
                  </Box>

                  {deliveryInstructions && (
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Typography
                        variant='subtitle2'
                        fontWeight='bold'
                        color='primary.main'
                        sx={{ mb: 0.5 }}
                      >
                        Delivery Instructions
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ color: 'text.secondary', wordBreak: 'break-word' }}
                      >
                        {deliveryInstructions}
                      </Typography>
                    </Box>
                  )}
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <EScriptOrdersProgressTab
                    initialTab='events'
                    requestId={requestId}
                    referenceId={referenceId}
                    mpi={patient?.mpi}
                    orderId={orderId}
                  />
                </Box>
              </Grid>
            </Grid>

            <Grid item md={5}>
              <Grid sx={{ pb: 2, pt: 4, pl: 4 }}>
                <Grid container direction='row' alignItems='center'>
                  <Typography variant='subtitle2' fontWeight='bold' color='primary.main'>
                    Prescription(s)
                  </Typography>
                </Grid>
                {map(prescription, (prescriptionData) => {
                  const prescriptionId = prescriptionData?.prescriptionId;
                  const isExpanded = expandedPrescriptions[prescriptionId] || false;

                  return (
                    <Accordion
                      key={prescriptionId}
                      expanded={isExpanded}
                      onChange={() => togglePrescriptionExpansion(prescriptionId)}
                      sx={{
                        border: '1px solid #E0E0E0',
                        borderTop: 'none',
                        borderLeft: 'none',
                        '&:before': {
                          display: 'none',
                        },
                        '&.Mui-expanded': {
                          margin: 0,
                        },
                        boxShadow: 'none',
                        marginBottom: 1,
                      }}
                    >
                      <AccordionSummary sx={{ border: 'none' }}>
                        <Grid container justifyContent='space-between' alignItems='center'>
                          <Grid container alignItems='center' sx={{ flex: 1 }}>
                            <Typography>
                              {`${prescriptionData?.prescriptionType} - ${
                                prescriptionData?.description
                              } ${
                                prescriptionData?.strength ? `- ${prescriptionData?.strength}` : ''
                              } `}
                            </Typography>

                            <Tooltip
                              title={prescriptionData?.reviewReason ? 'Under Review' : ''}
                              arrow
                            >
                              <InfoIcon sx={{ ml: 1, color: 'info.main' }} />
                            </Tooltip>
                          </Grid>

                          <IconButton aria-label='Toggle Details prescription'>
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </Grid>
                      </AccordionSummary>

                      <AccordionDetails sx={{ pt: 0 }}>
                        {prescriptionData?.reviewReason && (
                          <Typography fontWeight='bold' sx={{ pb: 1 }}>
                            Review Type : {formatReviewReason(prescriptionData.reviewReason)}
                          </Typography>
                        )}
                        <Grid container direction='row' alignItems='center' sx={{ pb: 1 }}>
                          <Typography variant='subtitle2' fontWeight='bold' color='primary.main'>
                            Prescription Information
                          </Typography>
                          <IconButton
                            aria-label='Toggle Details prescription details'
                            onClick={() =>
                              upsertViewMoreElements(fieldToggleHandler.PrescriptionDetails)
                            }
                          >
                            {isOpenPrescriptionDetails ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        </Grid>
                        <ItemLabelValue label='Description' value={prescriptionData?.description} />
                        <ItemLabelValue label='Strength' value={prescriptionData?.strength} />
                        <Collapse in={isOpenPrescriptionDetails} timeout='auto' unmountOnExit>
                          <ItemLabelValue
                            label='Expiration Date'
                            value={prescriptionData?.expirationDate}
                          />
                          <ItemLabelValue label='DAW' value={prescriptionData?.dispenseAsWritten} />
                          <ItemLabelValue label='Directions' value={prescriptionData?.directions} />
                          <ItemLabelValue label='Quantity' value={prescriptionData?.quantity} />
                          <ItemLabelValue label='Refills' value={prescriptionData?.totalRefills} />
                          <ItemLabelValue
                            label='Ship By Date'
                            value={formatShipByDate(prescriptionData?.shipByDate)}
                          />
                          <ItemLabelValue
                            label='Needs By Date (Legacy)'
                            value={prescriptionData?.needsByDate}
                          />
                          <ItemLabelValue label='Notes' value={prescriptionData?.notes} />
                        </Collapse>
                        <Grid container direction='row' alignItems='center' sx={{ pb: 1 }}>
                          <Typography variant='subtitle2' fontWeight='bold' color='primary.main'>
                            Originating Pharmacy
                          </Typography>

                          <IconButton
                            aria-label='Toggle Details pharmacy'
                            onClick={() => upsertViewMoreElements(fieldToggleHandler.Pharmacy)}
                          >
                            {isOpenPharmacy ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </Grid>

                        <ItemLabelValue
                          label='Name'
                          value={prescriptionData.originatingPharmacy?.name}
                        />
                        <ItemLabelValue
                          label='Pharmacist'
                          value={prescriptionData.originatingPharmacy?.pharmacistName}
                        />
                        <Collapse in={isOpenPharmacy} timeout='auto' unmountOnExit>
                          <ItemLabelValue
                            label='DEA'
                            value={prescriptionData.originatingPharmacy?.dea}
                          />
                          <ItemLabelValue
                            label='NPI'
                            value={prescriptionData.originatingPharmacy?.npi}
                          />
                          <ItemLabelValue
                            label='Phone'
                            value={prescriptionData.originatingPharmacy?.phoneNumber}
                          />
                          <ItemLabelValue
                            label='Address'
                            value={[
                              prescriptionData.originatingPharmacy?.pharmacyAddress?.addressLine1,
                              prescriptionData.originatingPharmacy?.pharmacyAddress?.addressLine2,
                              prescriptionData.originatingPharmacy?.pharmacyAddress?.addressCity,
                              prescriptionData.originatingPharmacy?.pharmacyAddress?.addressState,
                              prescriptionData.originatingPharmacy?.pharmacyAddress?.addressZip,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          />
                        </Collapse>
                        <Grid container direction='row' alignItems='center' sx={{ pb: 1 }}>
                          <Typography variant='subtitle2' fontWeight='bold' color='primary.main'>
                            Prescribed By
                          </Typography>
                          <IconButton
                            aria-label='Toggle Details provider details'
                            onClick={() =>
                              upsertViewMoreElements(fieldToggleHandler.ProviderDetails)
                            }
                          >
                            {isOpenProviderDetails ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        </Grid>
                        <ItemLabelValue
                          label='Title'
                          value={prescriptionData?.prescribedBy?.title}
                        />
                        <ItemLabelValue label='Name' value={prescriptionData?.prescribedBy?.name} />
                        <Collapse in={isOpenProviderDetails} timeout='auto' unmountOnExit>
                          <ItemLabelValue label='DEA' value={prescriptionData?.prescribedBy?.dea} />
                          <ItemLabelValue label='NPI' value={prescriptionData?.prescribedBy?.npi} />
                          <ItemLabelValue
                            label='Phone Number'
                            value={prescriptionData?.prescribedBy?.phoneNumber}
                          />
                          <ItemLabelValue
                            label='Address'
                            value={[
                              prescriptionData?.prescribedBy?.address?.addressLine1,
                              prescriptionData?.prescribedBy?.address?.addressLine2,
                              prescriptionData?.prescribedBy?.address?.addressCity,
                              prescriptionData?.prescribedBy?.address?.addressState,
                              prescriptionData?.prescribedBy?.address?.addressZip,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          />
                        </Collapse>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Grid>
              <Grid sx={{ pb: 2, pt: 2, pl: 4 }}>
                <Typography variant='subtitle2' fontWeight='bold' color='primary.main'>
                  Transfer Details
                </Typography>
                {isEmpty(transferPharmacies) && (
                  <Typography sx={{ pt: 3, ml: 3 }}>No transfer details found</Typography>
                )}
                {!isEmpty(transferPharmacies) &&
                  map(transferPharmacies, (transferPharmacy, index) => {
                    const pharmacyKey = `Pharmacy${transferPharmacy.npi}-${index}`;
                    const isOpenDynamic = dynamicOpenStates[`isOpen${pharmacyKey}`];

                    return (
                      <Box key={pharmacyKey}>
                        <Grid container direction='row' alignItems='center' sx={{ pb: 1 }}>
                          <Typography variant='subtitle2' fontWeight='bold' color='primary.main'>
                            Transfer Pharmacy
                          </Typography>
                          <IconButton
                            aria-label='Toggle Details Transfer pharmacy'
                            onClick={() => upsertViewMoreElements(pharmacyKey)}
                          >
                            {isOpenDynamic ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </Grid>

                        <ItemLabelValue label='Name' value={transferPharmacy?.name} />
                        <ItemLabelValue
                          label='Pharmacist'
                          value={transferPharmacy?.pharmacistName}
                        />
                        <Collapse in={isOpenDynamic} timeout='auto' unmountOnExit>
                          <ItemLabelValue label='DEA' value={transferPharmacy?.dea} />
                          <ItemLabelValue label='NPI' value={transferPharmacy?.npi} />
                          <ItemLabelValue
                            label='Transfer Date'
                            value={
                              transferPharmacy?.transferDate
                                ? format(
                                    new Date(transferPharmacy.transferDate),
                                    'MM/dd/yyyy hh:mmaaa'
                                  )
                                : undefined
                            }
                          />
                          <ItemLabelValue label='Phone' value={transferPharmacy?.phoneNumber} />
                          <ItemLabelValue
                            label='Pharmacy Address'
                            value={[
                              transferPharmacy?.pharmacyAddress?.addressLine1,
                              transferPharmacy?.pharmacyAddress?.addressLine2,
                              transferPharmacy?.pharmacyAddress?.addressCity,
                              transferPharmacy?.pharmacyAddress?.addressState,
                              transferPharmacy?.pharmacyAddress?.addressZip,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          />
                        </Collapse>
                      </Box>
                    );
                  })}
              </Grid>
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

EScriptRequestView.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  requestId: PropTypes.string.isRequired,
  referenceId: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  mpi: PropTypes.string.isRequired,
  fromPatientProfile: PropTypes.bool,
  partnerId: PropTypes.string,
};

EScriptRequestView.defaultProps = {
  fromPatientProfile: false,
  partnerId: undefined,
};

export default EScriptRequestView;

const ItemLabelValue = ({ value, label, verificationTag, labelAdornment, valueAdornment }) => {
  const theme = useTheme();

  return (
    <Grid container sx={{ pb: 1 }}>
      <Grid item sx={{ minWidth: 200, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography variant='body1' sx={{ color: lighten(theme.palette.text.primary) }}>
          {label}
        </Typography>
        {labelAdornment}
      </Grid>
      <Grid item sx={{ minWidth: 100, display: 'flex', alignItems: 'center' }}>
        <Typography variant='body1'>{value || 'NA'}</Typography>
        {value && valueAdornment}
        {verificationTag && <VerifiedTag status={ProviderVerificationStatus[verificationTag]} />}
      </Grid>
    </Grid>
  );
};

ItemLabelValue.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  verificationTag: PropTypes.string,
  labelAdornment: PropTypes.node,
  valueAdornment: PropTypes.node,
};

ItemLabelValue.defaultProps = {
  label: '',
  value: 'NA',
  verificationTag: undefined,
  labelAdornment: null,
  valueAdornment: null,
};
